import { useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { CmsApi, insertInArrayByIndex, isEmptyObject, useSettings } from '@atlas/core';

import updateMeta from './updateMeta';
import { getCurrentPage } from './utils';

const SET_STATE = 'SET_STATE';

const initialState = {
  currentPage: null,
  error: null,
  isLoading: true,
  selectedPageIndex: -1,
};

const reducer = (state, action) => {
  switch (action.type) {
    case SET_STATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

const useSeoPageState = ({ disabledRoutes = null } = {}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [pageListConfig, setPageListConfig] = useState();
  const location = useLocation();
  const { lang } = useSettings();

  useEffect(() => {
    CmsApi.getPageList({ lang })
      .then(pageList => {
        setPageListConfig(pageList);
        // Reset selected page data
        dispatch({
          payload: {
            currentPage: null,
            error: null,
            selectedPageIndex: -1,
          },
          type: SET_STATE,
        });
      })
      .catch(err =>
        dispatch({
          payload: {
            error: err,
          },
          type: SET_STATE,
        }),
      );

    return () => {
      // Reset page configuration on language switch or unmount
      setPageListConfig(null);
    };
  }, [lang]);

  useEffect(() => {
    updateMeta(state.currentPage);
  }, [state.currentPage]);

  useEffect(() => {
    // Wait for pages configuration
    if (!pageListConfig) return;

    const { currentPage, currentPageIndex } = getCurrentPage(
      pageListConfig,
      decodeURIComponent(location.pathname),
      location.search,
    );

    // Skip if selecting same page index
    if (currentPageIndex === state.selectedPageIndex) {
      return;
    }

    // If data is already fetched I do not need to load it again
    if (currentPage == null || currentPage.data) {
      dispatch({
        payload: {
          currentPage,
          error: null,
          isLoading: false,
          // Save the index of selected pages
          selectedPageIndex: currentPageIndex,
        },
        type: SET_STATE,
      });

      return;
    }

    if (!disabledRoutes || !disabledRoutes.find(route => location.pathname.indexOf(route) > -1)) {
      dispatch({
        payload: {
          error: null,
          isLoading: true,
          // Save the index of the selected page that will be loaded
          selectedPageIndex: currentPageIndex,
        },
        type: SET_STATE,
      });

      CmsApi.getPage({
        lang,
        source: currentPage.slug,
      })
        .then(loadedPage => {
          if (isEmptyObject(loadedPage)) {
            throw new Error('Page not found');
          }

          // Store loaded page data as current page
          dispatch({
            payload: {
              currentPage: loadedPage,
              error: null,
              isLoading: false,
            },
            type: SET_STATE,
          });

          // Update pages cache
          setPageListConfig(pageList =>
            insertInArrayByIndex(pageList, currentPageIndex, loadedPage),
          );
        })
        .catch(error => {
          dispatch({
            payload: {
              currentPage: null,
              error,
              isLoading: false,
            },
            type: SET_STATE,
          });
        });
    } else {
      updateMeta();
    }
  }, [location, lang, disabledRoutes, pageListConfig, state.selectedPageIndex]);

  return state;
};

useSeoPageState.propTypes = {
  disabledRoutes: PropTypes.array,
};

export default useSeoPageState;
